import { useCallback, useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { useFormMode } from '../contexts/FormModeContext';
import { getDynamicSectionType, extractSectionData } from '../utils/sectionMapping';
import { hasBlockingSaveErrors } from '../utils/validationUtils';

interface UseFormSaveOptions {
  stepId: string;
  autoSaveDelay?: number;
}

export const useFormSave = ({
  stepId,
  autoSaveDelay = 5000,
}: UseFormSaveOptions) => {
  const { mode, onSave, autoSaveEnabled, applicationId } = useFormMode();
  const { watch, getValues, formState: { errors } } = useFormContext();
  const saveTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isUpdatingRef = useRef(false);

  // Manual save function
  const saveForm = useCallback(
    async (formData?: any, skipValidationCheck = false) => {
      // Check for blocking validation errors unless explicitly skipped
      if (!skipValidationCheck && hasBlockingSaveErrors(errors)) {
        console.warn('Save blocked due to validation errors that indicate invalid data');
        return { success: false, reason: 'validation_errors' };
      }

      if (mode === 'static') {
        // Static mode: save to localStorage
        const currentData = formData || getValues();
        const existingData = localStorage.getItem('applicationForm');
        const fullFormData = existingData ? JSON.parse(existingData) : {};

        // Update the specific section
        fullFormData[stepId] = currentData;
        fullFormData.lastSaved = new Date().toISOString();

        localStorage.setItem('applicationForm', JSON.stringify(fullFormData));
        return { success: true };
      }
      // Dynamic mode: save to backend
      if (mode === 'dynamic' && onSave && applicationId) {
        const fullFormData = formData || getValues();
        const sectionSpecificData = extractSectionData(stepId, fullFormData);
        const sectionType = getDynamicSectionType(stepId);
        await onSave(sectionSpecificData, sectionType);
        return { success: true };
      }

      return { success: false, reason: 'no_save_context' };
    },
    [mode, onSave, applicationId, stepId, getValues, errors]
  );

  // Auto-save functionality
  useEffect(() => {
    if (!autoSaveEnabled || mode !== 'dynamic') return;

    const subscription = watch((_, { name, type }) => {
      // Skip if we're programmatically updating the form
      if (isUpdatingRef.current) return;

      // Only auto-save if this was a user change
      if (type === 'change' && name) {
        // Clear any existing timer
        if (saveTimerRef.current) {
          clearTimeout(saveTimerRef.current);
        }

        // Set new timer
        saveTimerRef.current = setTimeout(() => {
          // Auto-save will be blocked if there are validation errors that indicate invalid data
          saveForm();
        }, autoSaveDelay);
      }
    });

    return () => {
      subscription.unsubscribe();
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
      }
    };
  }, [watch, saveForm, autoSaveEnabled, mode, autoSaveDelay, stepId]);

  // Load initial data function
  const loadInitialData = useCallback(() => {
    if (mode === 'static') {
      const savedData = localStorage.getItem('applicationForm');
      if (savedData) {
        const parsedData = JSON.parse(savedData);
        return parsedData[stepId] || {};
      }
    }
    // For dynamic mode, data loading is handled by the parent component
    return {};
  }, [mode, stepId]);

  // Set updating flag (used when programmatically updating form)
  const setUpdating = useCallback((updating: boolean) => {
    isUpdatingRef.current = updating;
  }, []);

  return {
    saveForm,
    loadInitialData,
    setUpdating,
    mode,
    autoSaveEnabled,
    hasBlockingErrors: hasBlockingSaveErrors(errors),
  };
};
